import React, { useState, useEffect, createContext, useContext } from "react";
import {
  Route,
  Routes,
  useLocation
} from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { languageStorage, unveilStorage } from "./utils/localStorage";

// Core Components & Pages
import Navbar, { NavbarAnimationContext } from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import LoadingScreen from "./components/LoadingScreen";
import Home from "./pages/Home";
import About from "./pages/About";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";
import Projects from "./pages/Projects";
import Technology from "./pages/Technology";
import Applications from "./pages/Applications";
import ExploreMotor from "./pages/ExploreMotor";
import AxialFluxVsRadialFlux from "./pages/AxialFluxVsRadialFlux";

// Legal Pages
import TermsOfService from "./pages/legal/TermsOfService";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import CookiePolicy from "./pages/legal/CookiePolicy";

// Create VideoLoading Context
export const VideoLoadingContext = createContext();

export const useVideoLoading = () => useContext(VideoLoadingContext);

function AppContent() {
  const { i18n, ready } = useTranslation();
  const location = useLocation();
  const [videosLoading, setVideosLoading] = useState(true);
  const [loadingTimeoutPassed, setLoadingTimeoutPassed] = useState(false);

  // State for controlling navbar animation
  const [navbarShouldAnimate, setNavbarShouldAnimate] = useState(false);

  // State for controlling footer visibility based on unveil status and scroll position
  const [shouldShowFooter, setShouldShowFooter] = useState(true);
  const [hasUnveiled, setHasUnveiled] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  // Determine if we're on the home page
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const storedLang = languageStorage.get();
    if (storedLang && i18n.language !== storedLang) {
      i18n.changeLanguage(storedLang);
    }
  }, [i18n]);

  // Handle scroll tracking for home page footer visibility
  useEffect(() => {
    if (!isHomePage) return;

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  // Check unveil status and control footer visibility
  useEffect(() => {
    if (isHomePage) {
      const unveiled = unveilStorage.get();
      setHasUnveiled(unveiled);

      // On home page, footer should only show if unveiled AND user has scrolled down significantly
      if (unveiled) {
        // Show footer only when user has scrolled down at least 80% of viewport height
        const shouldShow = scrollY > window.innerHeight * 0.8;
        setShouldShowFooter(shouldShow);
      } else {
        setShouldShowFooter(false);
      }
    } else {
      setShouldShowFooter(true);
    }
  }, [isHomePage, scrollY, hasUnveiled]);

  // Listen for unveil status changes
  useEffect(() => {
    const handleUnveilStatusChange = (event) => {
      if (isHomePage && event.detail.hasUnveiled) {
        setHasUnveiled(true);
        // Don't immediately show footer - let scroll position control it
      }
    };

    window.addEventListener('unveilStatusChanged', handleUnveilStatusChange);

    return () => {
      window.removeEventListener('unveilStatusChanged', handleUnveilStatusChange);
    };
  }, [isHomePage]);

  // Show navbar after a short delay when app first loads, 
  // unless we're on the home page (where IntroAnimation will control it)
  useEffect(() => {
    if (location.pathname !== '/') {
      setTimeout(() => {
        setNavbarShouldAnimate(true);
      }, 300);
    }
  }, [location.pathname]);

  useEffect(() => {
    // Set a timeout to ensure content is shown after maximum 2 seconds
    // even if videos are still loading
    const timer = setTimeout(() => {
      setLoadingTimeoutPassed(true);
    }, 2000);

    // Clean up the timer if component unmounts
    return () => clearTimeout(timer);
  }, []);

  if (!ready || (videosLoading && !loadingTimeoutPassed)) {
    return <LoadingScreen />;
  }

  return (
    <NavbarAnimationContext.Provider value={{ navbarShouldAnimate, setNavbarShouldAnimate }}>
      <VideoLoadingContext.Provider value={{ videosLoading, setVideosLoading }}>
        <div className="App flex flex-col min-h-screen relative">
          {/* Consistent background for all pages */}
          <div className="fixed inset-0 w-full h-full">
            {/* Background image for all pages */}
            <div
              className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: 'url(/images/home-bg.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center center',
              }}
            />

            {/* Gradient overlay - different for home vs other pages */}
            <div className={`absolute inset-0 w-full h-full ${isHomePage
              ? 'bg-gradient-to-b from-[#e9ecef]/90 via-[#e9ecef]/85 via-[#dee2e6]/90 to-[#dee2e6]'
              : 'bg-gradient-to-b from-[#e9ecef]/90 via-[#e9ecef]/85 via-[#dee2e6]/90 to-[#dee2e6]'
              }`} />
          </div>

          <ScrollToTop />
          <Navbar />
          <main className="flex-grow relative z-10 pt-4">
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/technology" element={<Technology />} />
              <Route path="/applications" element={<Applications />} />
              <Route path="/explore-motor" element={<ExploreMotor />} />
              <Route path="/axial-flux-vs-radial-flux" element={<AxialFluxVsRadialFlux />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />

              {/* Legal routes */}
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/cookies" element={<CookiePolicy />} />

              {/* Catch-all Not Found Route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          {shouldShowFooter && <Footer />}
        </div>
      </VideoLoadingContext.Provider>
    </NavbarAnimationContext.Provider>
  );
}

function App() {
  return (
    <AppContent />
  );
}

export default App;